export const DAYTIME_START_HOUR = 6;
export const DAYTIME_END_HOUR = 19;

const clockFormatterCache = new Map();
const partsFormatterCache = new Map();

function formatterFor(cache, timeZone, options) {
  if (!cache.has(timeZone)) {
    cache.set(timeZone, new Intl.DateTimeFormat("en-US", { timeZone, ...options }));
  }

  return cache.get(timeZone);
}

export function localTimeParts(timeZone, now = new Date()) {
  if (typeof timeZone !== "string" || !timeZone.trim()) {
    return { valid: false, time: "Timezone unavailable" };
  }

  try {
    const normalizedTimeZone = timeZone.trim();
    const time = formatterFor(clockFormatterCache, normalizedTimeZone, {
      hour: "numeric",
      minute: "2-digit",
    }).format(now).replace(" ", "").toLowerCase();
    const parts = formatterFor(partsFormatterCache, normalizedTimeZone, {
      hour: "2-digit",
      minute: "2-digit",
      hourCycle: "h23",
    }).formatToParts(now);
    const hour = Number(parts.find((part) => part.type === "hour")?.value);
    const minute = Number(parts.find((part) => part.type === "minute")?.value);

    if (!Number.isInteger(hour) || !Number.isInteger(minute)) {
      return { valid: false, time: "Timezone unavailable" };
    }

    return {
      valid: true,
      time,
      hour,
      minute,
      hourFraction: hour + minute / 60,
      daytime: hour >= DAYTIME_START_HOUR && hour < DAYTIME_END_HOUR,
    };
  } catch (_) {
    return { valid: false, time: "Timezone unavailable" };
  }
}

export function localTimePeriod(hour) {
  if (hour >= DAYTIME_START_HOUR && hour < 12) return "Morning";
  if (hour >= 12 && hour < 17) return "Afternoon";
  if (hour >= 17 && hour < DAYTIME_END_HOUR) return "Early evening";
  if (hour >= DAYTIME_END_HOUR && hour < 23) return "Evening";
  return "Night";
}

export function groupTeamByLocalTime(team, now = new Date()) {
  const groupsByMinute = new Map();
  const invalidMembers = [];

  team.forEach((member) => {
    const local = localTimeParts(member.tz, now);
    if (!member.tzValid || !local.valid) {
      invalidMembers.push(member);
      return;
    }

    const key = local.hour * 60 + local.minute;
    const group = groupsByMinute.get(key) || { ...local, members: [], timezoneLabels: new Set() };
    group.members.push(member);
    if (member.tzLabel) group.timezoneLabels.add(member.tzLabel);
    groupsByMinute.set(key, group);
  });

  const groups = Array.from(groupsByMinute.values())
    .map((group) => ({
      ...group,
      timezoneLabels: undefined,
      label: Array.from(group.timezoneLabels).sort().join(" · ") || "Local time",
    }))
    .sort((a, b) => a.hourFraction - b.hourFraction);

  return { groups, invalidMembers };
}

export function shouldUseListLayout(width, groups, forceCompact = false) {
  if (forceCompact || width < 760) return true;
  if (groups.some((group) => group.members.length > 6)) return true;

  const cardsThatFit = Math.max(1, Math.floor((width + 12) / 192));
  return groups.length > cardsThatFit;
}
